/**
 * Participant Success Component
 * Shows successful enrollment completion
 */

import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Home,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface LocationState {
  participantId?: string;
  studyCode?: string;
  email?: string;
}

const ParticipantSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const handleReturnHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
          
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
            🎉 Enrollment Complete!
          </Typography>
          
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Thank you for participating in our research study
          </Typography>

          <Card sx={{ mb: 4, backgroundColor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Your enrollment has been successfully completed!</strong>
              </Typography>
              <Typography variant="body2">
                Email verified<br />
                Fitbit account connected<br />
                Data collection authorized<br />
                Confirmation email sent
              </Typography>
            </CardContent>
          </Card>

          {state?.participantId && (
            <>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Your Participant ID:</strong>
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, fontFamily: 'monospace', backgroundColor: 'grey.100', p: 1, borderRadius: 1 }}>
                {state.participantId}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                Please save this ID for your records
              </Typography>
            </>
          )}

          <Card sx={{ mb: 4, backgroundColor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Typography variant="body2">
                <strong>What happens next?</strong><br />
                • Your Fitbit data will be automatically collected according to the study protocol<br />
                • You may receive periodic check-in emails from the research team<br />
                • Continue wearing your Fitbit device as normal<br />
                • You can withdraw from the study at any time by contacting the research team
              </Typography>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              onClick={handleReturnHome}
            >
              Return to Home
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            If you have any questions about the study, please contact the research team.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default ParticipantSuccess;
